import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Wallet, ExternalLink, Award, CalendarClock, FileText, Info, Search,
  Rocket, Building2, Users, CheckCircle, ArrowRight,
} from 'lucide-react';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FAFSA and NC-specific financial-aid deadlines, refreshed each year in seed data.
// Kept static because they don't change day-to-day and the user wants deadlines
// visible immediately without waiting on an API.
const AID_DEADLINES = [
  { key: 'fafsa_open',    label: 'FAFSA opens',                  when: 'Oct 1, 2026', kind: 'Federal',   pill: 'Open now' },
  { key: 'nc_reach',      label: 'NC Reach scholarship deadline', when: 'Mar 1, 2027', kind: 'State',     pill: null },
  { key: 'nc_need_based', label: 'NC Need-Based Scholarship',    when: 'Rolling',     kind: 'State',     pill: null },
  { key: 'fafsa_priority',label: 'Priority FAFSA filing',        when: 'Mar 15, 2027', kind: 'Federal',   pill: 'Recommended' },
  { key: 'college_gate',  label: 'Most college aid deadlines',   when: 'Feb-Apr 2027', kind: 'Institutional', pill: null },
  { key: 'cfnc',          label: 'CFNC scholarship applications', when: 'Rolling',    kind: 'State',      pill: null },
];

const AID_TYPES = [
  {
    key: 'fafsa', title: 'FAFSA (Federal Aid)', hue: '#22d3ee', icon: FileText,
    covers: 'Pell Grants, subsidised & unsubsidised loans, work-study, every college in the country reads it.',
    who: 'Every senior applying to college. File once, colleges and states use it.',
    url: 'https://studentaid.gov/h/apply-for-aid/fafsa',
    urgent: true,
  },
  {
    key: 'pell', title: 'Federal Pell Grant', hue: '#a78bfa', icon: Award,
    covers: 'Up to $7,395 / year (2026-27). Grants, you never pay them back.',
    who: 'Students whose family income falls below a federal threshold. FAFSA decides.',
    url: 'https://studentaid.gov/understand-aid/types/grants/pell',
  },
  {
    key: 'workforce_pell', title: 'Workforce Pell (short-term training)', hue: '#38bdf8', icon: Rocket,
    covers: 'NEW in 2026, Pell now covers 8-15 week job-training programs (welding, EMT, IT certs, etc.).',
    who: 'Anyone enrolled in a qualifying short-term workforce program.',
    url: 'https://studentaid.gov/understand-aid/types/grants/pell',
  },
  {
    key: 'nc_state', title: 'NC State Grants', hue: '#f472b6', icon: Building2,
    covers: 'NC Need-Based Scholarship, NC Reach, Golden LEAF, and community-college programs.',
    who: 'NC residents attending an NC public college or community college.',
    url: 'https://www.cfnc.org/pay-for-college/scholarships/',
  },
  {
    key: 'institutional', title: 'College Financial Aid', hue: '#c084fc', icon: Award,
    covers: 'Merit and need aid awarded directly by the college you attend.',
    who: 'Every admitted student, colleges each set their own priority deadlines.',
    url: 'https://bigfuture.collegeboard.org/pay-for-college/financial-aid-101',
  },
  {
    key: 'scholarships', title: 'Private Scholarships', hue: '#fbbf24', icon: Users,
    covers: 'Local businesses, community foundations, corporate programs. Averages $2k-$10k.',
    who: 'Every student. The catalog knows which match your GPA, major, and hometown.',
    url: null, internal: '/app/scholarships',
  },
];

const KIND_HUE = {
  Federal: '#22d3ee',
  State: '#a78bfa',
  Institutional: '#f472b6',
};

// The "Not for freshmen/sophomores yet" screen, Aid becomes real in 11th grade.
function EarlyGradeIntro({ grade }) {
  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6 pb-12" data-testid="funding-early-grade-intro">
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 p-6 md:p-8"
          style={{ boxShadow: '0 40px 100px -60px rgba(34,211,238,0.55)' }}
        >
          <div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full blur-3xl opacity-30"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute -left-24 -bottom-32 h-64 w-64 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.35), transparent 65%)' }} />

          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-0.5
              text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
              <Wallet className="h-3 w-3" /> Aid & FAFSA
            </span>
            <h1 className="font-outfit mt-3 text-3xl sm:text-4xl font-semibold text-white tracking-tight">
              Financial aid starts in{' '}
              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">
                junior year
              </span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300/90 leading-relaxed">
              You're in {grade === 9 ? '9th' : '10th'} grade, a little early for FAFSA and aid deadlines. Come back at the start of
              junior year and this page will unlock all your federal, state, and college aid options.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/app/scholarships"
                data-testid="funding-early-scholarships"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
                style={{ boxShadow: '0 0 22px -6px rgba(34,211,238,0.55)' }}>
                Explore scholarships anyway <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/app/tuition-calculator"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-cyan-300/30 text-cyan-100 hover:border-cyan-300/60 transition-colors">
                See what college could cost you
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/[0.05] p-5">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">This year</p>
            <p className="mt-1 text-sm font-semibold text-white">Keep your grades up</p>
            <p className="mt-1 text-xs text-slate-300 leading-relaxed">Every point in your GPA moves you into merit-aid bands worth thousands.</p>
          </div>
          <div className="rounded-2xl border border-violet-400/20 bg-violet-500/[0.05] p-5">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">Next year (11th)</p>
            <p className="mt-1 text-sm font-semibold text-white">FAFSA opens Oct 1</p>
            <p className="mt-1 text-xs text-slate-300 leading-relaxed">This page unlocks then. We'll walk you through every form.</p>
          </div>
          <div className="rounded-2xl border border-pink-400/20 bg-pink-500/[0.05] p-5">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">Senior year</p>
            <p className="mt-1 text-sm font-semibold text-white">Money hits your account</p>
            <p className="mt-1 text-xs text-slate-300 leading-relaxed">Pell, state grants, college aid, private scholarships, all matched to your profile.</p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default function FundingPathPage() {
  const { user, getAuthHeader } = useAuth();
  const [arrived, setArrived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/intelligence/funding`, getAuthHeader());
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const grade = Number(user?.grade || user?.grade_level || 0);
  const isJuniorOrSenior = grade >= 11;

  const filteredScholarships = useMemo(() => {
    const all = data?.scholarships || [];
    if (!q.trim()) return all;
    const s = q.toLowerCase();
    return all.filter((sc) =>
      (sc.scholarship_name || '').toLowerCase().includes(s) ||
      (sc.provider_name || '').toLowerCase().includes(s) ||
      (sc.description || '').toLowerCase().includes(s)
    );
  }, [data, q]);

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  // Freshmen and sophomores see a lighter intro page instead of forms they can't file yet.
  if (!isJuniorOrSenior && grade > 0 && grade < 11) {
    return <EarlyGradeIntro grade={grade} />;
  }

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="funding-arrival"
        eyebrow="Aid & FAFSA"
        title="Somebody else may pay for this."
        subtitle="FAFSA, Pell, state grants, and private scholarships, matched to your situation, with the actual deadlines you can't miss."
        accent="#22d3ee"
        second="#a78bfa"
        ctaLabel="Show me my aid"
        narration={"This is Aid & FAFSA. Most students pay too much because nobody told them what they qualified for. We check federal Pell, state grants, college aid, and private scholarships against your situation, with the deadlines that decide whether the money is yours."}
        facts={[
          { icon: FileText, head: "FAFSA first",     sub: "The single form that unlocks federal, state, and most college aid" },
          { icon: CalendarClock, head: "Deadlines that matter", sub: "Miss one and the money's gone" },
          { icon: Award, head: "Matched scholarships", sub: "Filtered by your GPA, major and hometown" },
        ]}
        modules={[
          { icon: FileText, label: "FAFSA",              line: "Federal aid, Pell, and low-interest loans", hue: "#22d3ee" },
          { icon: Award, label: "Pell Grant",            line: "Up to $7,395/year, you never pay it back", hue: "#a78bfa" },
          { icon: Rocket, label: "Workforce Pell",       line: "NEW, short-term job-training funding",     hue: "#38bdf8" },
          { icon: Building2, label: "NC State grants",   line: "NC Reach, need-based, community-college aid", hue: "#f472b6" },
          { icon: Award, label: "College financial aid", line: "Every college's institutional programs",   hue: "#c084fc" },
          { icon: Users, label: "Private scholarships",  line: "Local, corporate, foundation, averages $2-10k", hue: "#fbbf24" },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-12" data-testid="funding-page">
        <PageHeader
          title="Aid & FAFSA"
          description="Federal, state, and college financial aid, with the deadlines that decide whether the money is yours."
        />

        {/* Deadlines strip, brand cyan glow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 p-5"
          style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.5)' }}
          data-testid="funding-deadlines"
        >
          <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.4), transparent 65%)' }} />
          <div className="relative flex items-center gap-2 mb-4">
            <CalendarClock className="h-4 w-4 text-cyan-300" />
            <h2 className="text-base font-semibold text-white">Deadlines that decide whether the money is yours</h2>
          </div>
          <div className="relative grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {AID_DEADLINES.map((d) => {
              const hue = KIND_HUE[d.kind] || '#22d3ee';
              return (
                <div key={d.key} className="rounded-xl border p-3"
                  style={{ borderColor: `${hue}33`, background: `${hue}0a` }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-slate-300">{d.kind}</p>
                    {d.pill && (
                      <span className="rounded-full border border-cyan-300/40 bg-cyan-400/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-cyan-200">
                        {d.pill}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-semibold text-white">{d.label}</p>
                  <p className="text-xs mt-0.5" style={{ color: hue }}>{d.when}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Aid types, every kind of money on one screen */}
        <div>
          <h2 className="font-outfit text-lg font-semibold text-white mb-3">Every kind of money, in one place</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="funding-aid-types">
            {AID_TYPES.map((a) => {
              const Icon = a.icon;
              const CardWrap = a.internal
                ? ({ children }) => <Link to={a.internal} className="block group">{children}</Link>
                : a.url
                  ? ({ children }) => <a href={a.url} target="_blank" rel="noreferrer" className="block group">{children}</a>
                  : ({ children }) => <div className="block group">{children}</div>;
              return (
                <CardWrap key={a.key}>
                  <div className="rounded-2xl border p-5 h-full transition-colors group-hover:border-cyan-300/50"
                    style={{
                      borderColor: `${a.hue}33`,
                      background: `${a.hue}0a`,
                      boxShadow: a.urgent ? `0 20px 60px -50px ${a.hue}` : 'none',
                    }}
                    data-testid={`aid-${a.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: a.hue }} />
                        <p className="text-sm font-semibold text-white">{a.title}</p>
                      </div>
                      {a.internal || a.url ? <ExternalLink className="h-3 w-3 text-slate-300 group-hover:text-cyan-300" /> : null}
                    </div>
                    <p className="mt-2 text-xs text-slate-300 leading-relaxed">{a.covers}</p>
                    <div className="mt-3 flex items-start gap-1.5">
                      <Info className="h-3 w-3 text-slate-300 mt-0.5 shrink-0" />
                      <p className="text-[11px] text-slate-300 leading-relaxed">{a.who}</p>
                    </div>
                  </div>
                </CardWrap>
              );
            })}
          </div>
        </div>

        {/* Matched scholarships from the catalog, with search */}
        {(data?.scholarships || []).length > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
                <Award className="h-4 w-4 text-cyan-300" />
                Scholarships matched to you
                <span className="text-xs text-slate-300 font-normal">{filteredScholarships.length} of {(data?.scholarships || []).length}</span>
              </h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-300" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="funding-scholarship-search"
                  placeholder="Search scholarships…"
                  className="input-dark pl-9 pr-3 py-2 text-sm w-64"
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2" data-testid="funding-scholarships">
              {filteredScholarships.map((s) => (
                <div key={s.id} className="rounded-2xl border border-cyan-500/20 bg-slate-900/40 p-4 hover:border-cyan-300/40 transition-colors"
                  style={{ boxShadow: '0 20px 60px -55px rgba(34,211,238,0.45)' }}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-sm font-semibold text-white">{s.scholarship_name}</p>
                      <p className="text-xs text-slate-300">{s.provider_name}</p>
                    </div>
                    <div className="text-right">
                      {(s.award_amount_max || s.award_amount_min) ? (
                        <p className="text-sm font-semibold text-cyan-200">
                          ${(s.award_amount_min || 0).toLocaleString()}
                          {s.award_amount_max && s.award_amount_max !== s.award_amount_min ? `-$${s.award_amount_max.toLocaleString()}` : ''}
                        </p>
                      ) : null}
                      {s.deadline_date && (
                        <p className="text-[11px] mt-0.5 inline-flex items-center gap-1 text-violet-300">
                          <CalendarClock className="h-3 w-3" /> {s.deadline_date}
                        </p>
                      )}
                    </div>
                  </div>
                  {s.description && <p className="text-xs text-slate-300 mt-2 line-clamp-2">{s.description}</p>}
                  {s.application_url && (
                    <a href={s.application_url} target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-white transition-colors">
                      Apply <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {(data?.archetypes?.length || data?.note) && (
          <p className="text-xs text-slate-300" data-testid="funding-archetypes">
            {data?.archetypes?.length ? (
              <>Matched to: {data.archetypes.map((a) => a.label).join(' · ')}. </>
            ) : null}
            {data?.note}
          </p>
        )}

        {/* Bottom CTA */}
        <div className="rounded-2xl border border-violet-400/25 bg-violet-500/[0.05] p-5 flex flex-wrap items-center justify-between gap-3"
          style={{ boxShadow: '0 20px 60px -50px rgba(167,139,250,0.5)' }}>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-violet-300 shrink-0" />
            <p className="text-sm text-slate-200">
              Ready to file? The FAFSA is one form. It unlocks federal, state, and every college's aid.
            </p>
          </div>
          <a href="https://studentaid.gov/h/apply-for-aid/fafsa" target="_blank" rel="noreferrer"
            data-testid="funding-file-fafsa"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
            style={{ boxShadow: '0 0 22px -8px rgba(34,211,238,0.6)' }}>
            Start my FAFSA <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
